<template>
  <div class="fill-grid-map" :class="{ 'aside-collapsed': asideCollapsed }">
    <div class="task-map" :class="{ 'transition-map': asideTransition }">
      <GmapMap v-if="mapProvider === MapProvider.GOOGLE"
        ref="gmap"
        style="height: 100%;"
        :zoom="this.initialZoom"
        :center="centerLocation"
        :options="{
          zoomControl: true,
          clickableIcons: false,
          mapTypeControl: true,
          scaleControl: false,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: true,
          disableDefaultUi: false,
          gestureHandling: 'greedy'
        }"
      >
        <GmapMarker
          v-for="({marker, locationType}, index) in tasksMarkers"
          :key='`task-${index}`'
          :position="marker"
          :title="marker.label"
          :icon="locationType === 'visited' ? `${basePath}img/maps/icon-1.png` : `${basePath}img/maps/icon-2.png`"
          @click="() => selectTask(marker.label ? marker.label : '')"
        />

        <GmapInfoWindow
          v-if="showLocationInfoWindow"
          :position="selectedLocation"
          :options="{ pixelOffset: {width: 0, height: -35} }"
          @closeclick="deselectTask"
        >
          <div class="map-task-info-window">
            <h1>{{ selectedLocationLabel }}</h1>
            <h2>Fecha de envío: {{ selectedTaskReceivedDate }}</h2>
            <a href="" @click.prevent="openTypistFromMap">Detalles</a>
          </div>
        </GmapInfoWindow>
      </GmapMap>
      <div v-else class="map-box-container">
        <div ref="mapBox" class="map-box"></div>
      </div>
    </div>
    <FillGridMapAside
      class="map-aside"
      :class="{ 'transition-aside': asideTransition }"
      :tasks="allTasks"
      :ident="idents[0]"
      :selected-task-id="selectedTaskID"
      @toggle-aside="() => asideCollapsed = !asideCollapsed"
      @select-task="selectTask"
    />
  </div>

</template>

<script>
// Typscript will not be used as it causes issues with mapbox-gl
import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator'
import mapboxgl from 'mapbox-gl'
import FillGridMapAside from './FillGridMapAside.vue'
import userInfoStore from '@/store/userInfo'
import { MarkerPosition } from '@/types/Maps'
import { MapProvider } from '@/types//Auth'
import config from '@/config'
import moment from 'moment'

@Component({
  components: {
    FillGridMapAside
  }
})

export default class FillGridMap extends Vue {
  @Prop({ required: true, default: () => [] })
  tasks

  @Prop({ required: true, default: () => [] })
  idents

  @Prop({ required: true, default: () => [] })
  identsLabels

  @Inject() openFillInTypistFromMap

  allTasks = []
  asideCollapsed = false
  selectedTask = null
  showLocationInfoWindow = false
  mapProvider = userInfoStore.userInfo.domain.mapProvider
  MapProvider = MapProvider
  basePath = config.basePath
  initialZoom = this.mapProvider === MapProvider.GOOGLE ? 6 : 5

  processTasks (tasks) {
    this.allTasks = tasks.map(task => {
      return {
        ...task,
        locationType: task._visitedLocation ? 'visited' : task._fillLocation ? 'planned' : 'no-location'
      }
    })
  }

  get tasksMarkers () {
    return this.allTasks.map((task, index) => {
      if (task.locationType !== 'no-location') {
        const location = task._visitedLocation || task._fillLocation
        return {
          marker: new MarkerPosition(location.latitude, location.longitude, null, task[this.idents[0]]),
          locationType: task.locationType,
          baseIndex: index
        }
      }
      return null
    }).filter(marker => marker !== null)
  }

  get selectedLocation () {
    return this.selectedTask && this.selectedTask._visitedLocation
      ? new MarkerPosition(
        this.selectedTask._visitedLocation.latitude,
        this.selectedTask._visitedLocation.longitude
      )
      : this.selectedTask && this.selectedTask._fillLocation
        ? new MarkerPosition(
          this.selectedTask._fillLocation.latitude,
          this.selectedTask._fillLocation.longitude
        )
        : null
  }

  createLocationLabel (task) {
    return this.idents.map((ident, index) => `${this.identsLabels[index]}: ${task[ident]}`).join(' - ')
  }

  get selectedLocationLabel () {
    return this.selectedTask
      ? this.createLocationLabel(this.selectedTask)
      : ''
  }

  createTaskReceivedDate (task) {
    return task._receivedDate
      ? moment(this.toUTCZuluTimestamp(task._receivedDate)).format('MMMM DD, YYYY - HH:mm')
      : '--'
  }

  get selectedTaskReceivedDate () {
    return this.selectedTask
      ? this.createTaskReceivedDate(this.selectedTask)
      : '--'
  }

  get selectedTaskID () {
    return this.selectedTask ? this.selectedTask._id : -1
  }

  get centerLocation () {
    return this.tasksMarkers.length > 0
      ? { lat: this.tasksMarkers[0].marker.lat, lng: this.tasksMarkers[0].marker.lng }
      : { lat: 19.4263, lng: -99.1266 }
  }

  get asideTransition () {
    return this.mapProvider === MapProvider.GOOGLE
  }

  selectTask (identValue) {
    let foundTask = null
    let foundIndex = -1
    this.allTasks.forEach((task, index) => {
      if (task[this.idents[0]] === identValue) {
        this.selectedTask = task
        foundTask = task
        foundIndex = index
        task.selected = true
      } else {
        task.selected = false
      }
    })

    if (!foundTask || foundTask.locationType === 'no-location') {
      this.deselectTask()
      return
    }

    if (this.mapProvider === MapProvider.GOOGLE) {
      this.showLocationInfoWindow = true
      this.$refs.gmap.panTo({
        lat: foundTask._visitedLocation ? foundTask._visitedLocation.latitude : foundTask._fillLocation.latitude,
        lng: foundTask._visitedLocation ? foundTask._visitedLocation.longitude : foundTask._fillLocation.longitude
      })
      this.$refs.gmap.$mapObject.setZoom(14)
    } else {
      this.mapboxMarkers.forEach(({ marker }) => {
        marker._popup.remove()
      })
      const mapboxMarker = this.mapboxMarkers.find(({ baseIndex }) => baseIndex === foundIndex)
      if (mapboxMarker) {
        mapboxMarker.marker._popup.addTo(this.map)
        this.map.flyTo({
          center: mapboxMarker.marker.getLngLat(),
          zoom: 14
        })
      }
      const toTypistButton = document.getElementById(foundTask[this.idents[0]])
      toTypistButton.addEventListener('click', (event) => {
        event.preventDefault()
        this.openFillInTypistFromMap(foundTask)
      })
    }
  }

  deselectTask () {
    this.selectedTask = null
    this.showLocationInfoWindow = false
    if (this.mapProvider === MapProvider.MAPBOX) {
      this.mapboxMarkers.forEach(({ marker }) => {
        marker._popup.remove()
      })
    }
  }

  openTypistFromMap () {
    if (this.selectedTask) {
      this.openFillInTypistFromMap(this.selectedTask)
    }
  }

  addMapboxMarkers (markers) {
    this.mapboxMarkers = markers.map(({ marker, locationType, baseIndex }) => {
      const element = document.createElement('div')
      element.className = 'mapbox-marker'

      const img = document.createElement('img')
      img.src = `${this.basePath}img/maps/icon-${locationType === 'visited' ? 1 : 2}.png`
      element.appendChild(img)

      const popup = new mapboxgl.Popup({ closeButton: true, offset: 20, closeOnClick: true })
        .setHTML(`
          <div class="map-task-info-window mapbox">
            <h1>${this.createLocationLabel(this.allTasks[baseIndex])}</h1>
            <h2>Fecha de envío: ${this.createTaskReceivedDate(this.allTasks[baseIndex])}</h2>
            <a href="" id="${this.allTasks[baseIndex][this.idents[0]]}">Detalles</a>
          </div>
        `)

      element.addEventListener('click', () => {
        const identValue = this.allTasks[baseIndex][this.idents[0]]
        this.selectTask(identValue)
      })

      const mapboxMarker = new mapboxgl.Marker(element)
        .setLngLat([marker.lng, marker.lat])
        .setPopup(popup)
        .addTo(this.map)

      return {
        marker: mapboxMarker,
        baseIndex
      }
    })
  }

  toUTCZuluTimestamp (date) {
    let timestamp = date.toString()
    return timestamp.endsWith('Z') ? timestamp : timestamp + 'Z'
  }

  // #################################
  // MAPBOX CONFIGURATION AND LOGIC
  // #################################

  mounted () {
    this.processTasks(this.tasks)
    if (this.mapProvider === MapProvider.MAPBOX) {
      let map = new mapboxgl.Map({
        container: this.$refs.mapBox,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [this.centerLocation.lng, this.centerLocation.lat],
        zoom: this.initialZoom
      })
      // Add UI control buttons
      map.addControl(new mapboxgl.NavigationControl(), 'bottom-right')

      this.map = map

      setTimeout(() => {
        this.map.resize()
        this.map.setLayoutProperty('country-label', 'text-field', ['get', 'name_es'])
      }, 100)

      this.map.on('load', () => {
        this.addMapboxMarkers(this.tasksMarkers)
      })
    }
  }

  @Watch('tasks')
  onTaskTasksChange (newTasks) {
    this.processTasks(newTasks)
    if (this.mapProvider === MapProvider.GOOGLE) {
      this.deselectTask()
    } else {
      this.mapboxMarkers.forEach(({ marker }) => marker.remove())
      this.addMapboxMarkers(this.tasksMarkers)
    }
  }

  @Watch('asideCollapsed')
  onAsideCollapsedChange () {
    if (this.mapProvider === MapProvider.MAPBOX) {
      setTimeout(() => {
        this.map.resize()
      }, 30)
    }
  }
}

</script>

<style lang="scss" scoped>
.fill-grid-map {
  position: relative;
  $right-aside-width: 272px;
  width: 100%;
  display: flex;
  overflow-x: hidden;

  .task-map {
    margin-right: $right-aside-width;
    width: 100%;
  }

  .transition-map {
    transition: margin-right 200ms ease;
  }

  .map-aside {
    position: absolute;
    top: 0;
    right: 0;
    width: $right-aside-width;
    background-color: var(--sidebar-bg-color);

  }

  .transition-aside {
    transition: right 200ms ease;
  }

  &.aside-collapsed {
    .map-aside {
      right: -$right-aside-width;
    }

    .task-map {
      margin-right: 0;
    }
  }

  .map-box-container {
    height: 100%;
    width: auto;

    .map-box {
      position: relative;
      width: 100%;
      height: 100%;
    }

  }
}
</style>

<style>
  .map-task-info-window {
    text-align: center;

    h1 {
      font-size: 0.85rem;
      border-bottom: 1px solid #ccc;
      padding-bottom: 5px;
    }

    h2 {
      font-size: 0.75rem;
      color: #919191;
    }
  }

  .mapboxgl-popup-content {
    width: max-content;
  }

  .map-task-info-window.mapbox {
    padding: 1rem 0 0 0;
  }
</style>
